import React, { useEffect, useState, useRef, lazy } from 'react';
import { Activity, Check, ArrowRight, BookOpen, Gift, Lock, Heart, Trophy, Star } from 'lucide-react';
import { PageType } from '../App';
interface SalesPageProps {
  navigate: (page: PageType) => void;
}
// CSS-only FadeIn with IntersectionObserver (replaces Framer Motion — saves 92 KiB)
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(el);
      }
    }, {
      rootMargin: '-50px'
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className={className} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
    willChange: isVisible ? 'auto' : 'opacity, transform'
  }} data-id="element-4">
      {children}
    </div>;
};
export function SalesPage({
  navigate
}: SalesPageProps) {
  const [selectedOffer, setSelectedOffer] = useState<'base' | 'pro'>('pro');
  const handleOfferKeyDown = (offer: 'base' | 'pro') => (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setSelectedOffer(offer);
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setSelectedOffer('pro');
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      setSelectedOffer('base');
    }
  };
  return <div className="min-h-screen bg-navy-900 font-sans text-offwhite overflow-x-hidden" data-id="element-5">
      {/* Skip to content — accessibility */}
      <a href="#main-content" className="skip-link" data-id="element-6">
        Saltar al contenido principal
      </a>

      <main id="main-content" data-id="element-7">
        {/* HERO SECTION */}
        <section aria-label="Presentación del producto" className="relative pt-12 pb-16 px-4 md:pt-20 md:pb-24 border-b border-gold-500/20 min-h-screen flex flex-col justify-center" data-id="element-8">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-navy-700/40 via-navy-900 to-navy-900 -z-10" aria-hidden="true" data-id="element-9"></div>
          <div className="max-w-5xl mx-auto text-center" data-id="element-10">
            <div className="animate-fadeIn" data-id="element-11">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif text-white leading-tight mb-8 drop-shadow-lg" data-id="element-12">
                ¿Cuántas veces más vas a aguantar el dolor esperando que se vaya
                solo?
              </h1>

              <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(212,175,55,0.15)] border border-gold-500/20 mb-10 animate-slideUp" data-id="element-13">
                <img src="https://cdn.magicpatterns.com/uploads/uVc1WwKfFGDgsMhcr3wxtx/ChatGPT_Image_16_mar_2026,_00_23_45.png" alt="Método Kinesio-Ahorro: colección completa de 6 guías para manejar tu dolor en casa — 6 tomos, 2 bonos, exclusivo para Argentina" className="w-full h-auto" width={896} height={512} fetchPriority="high" decoding="sync" sizes="(max-width: 768px) 100vw, (max-width: 1024px) 90vw, 896px" style={{
                aspectRatio: '7/4'
              }} data-id="element-14" />
              </div>

              <p className="text-xl md:text-2xl text-gray-300 font-normal max-w-3xl mx-auto leading-relaxed" data-id="element-15">
                Hay personas que se levantan de la cama de un salto. Y hay
                personas que necesitan 10 minutos para enderezarse. La
                diferencia no es la edad.{' '}
                <span className="text-gold-400 font-semibold italic" data-id="element-16">
                  Es si tienen un sistema o no.
                </span>
              </p>
            </div>
          </div>
        </section>

        {/* PROBLEM SECTION */}
        <section aria-label="El problema del dolor crónico" className="py-20 px-4 bg-navy-900" data-id="element-17">
          <div className="max-w-3xl mx-auto" data-id="element-18">
            <FadeIn data-id="element-19">
              <h2 className="text-3xl md:text-4xl font-bold text-gold-500 font-serif mb-12 text-center" data-id="element-20">
                EL PROBLEMA
              </h2>
            </FadeIn>

            <div className="space-y-6 text-lg md:text-xl text-gray-300 leading-relaxed font-normal" data-id="element-21">
              <FadeIn delay={0.1} data-id="element-22">
                <p data-id="element-23">Son las 7 de la mañana de un lunes.</p>
              </FadeIn>
              <FadeIn delay={0.2} data-id="element-24">
                <p data-id="element-25">Sonó el despertador.</p>
              </FadeIn>
              <FadeIn delay={0.3} data-id="element-26">
                <p data-id="element-27">Intentás incorporarte de la cama.</p>
              </FadeIn>
              <FadeIn delay={0.4} data-id="element-28">
                <p data-id="element-29">Y en exactamente 3 segundos, pasa lo que siempre pasa.</p>
              </FadeIn>
              <FadeIn delay={0.5} data-id="element-30">
                <p data-id="element-31">
                  El cuello está rígido. La cintura no afloja. Y el primer paso
                  del día ya duele antes de llegar al baño.
                </p>
              </FadeIn>

              <FadeIn delay={0.6} className="pt-4 space-y-2" data-id="element-32">
                <p className="text-gold-400 italic font-serif" data-id="element-33">
                  10 centímetros de movimiento.
                </p>
                <p className="text-gold-400 italic font-serif" data-id="element-34">
                  30 centímetros.
                </p>
                <p data-id="element-35">Y ya no podés doblarte para atarte los zapatos.</p>
              </FadeIn>

              <FadeIn delay={0.7} className="pt-4" data-id="element-36">
                <p data-id="element-37">
                  Esa sensación no es normal. No debería ser parte de cada
                  mañana.
                </p>
                <p data-id="element-38">Pero se convirtió en tu rutina.</p>
              </FadeIn>

              <FadeIn delay={0.2} className="pt-8" data-id="element-39">
                <p className="text-white font-medium mb-6" data-id="element-40">
                  Y cuando pasa, sabés exactamente lo que viene:
                </p>
                <ul className="space-y-4 pl-4 border-l-2 border-red-500/50" role="list" data-id="element-41">
                  <li className="flex items-start" role="listitem" data-id="element-42">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" aria-hidden="true" data-id="element-43" />{' '}
                    La aspirina o el ibuprofeno de siempre que te dura 4 horas
                  </li>
                  <li className="flex items-start" role="listitem" data-id="element-44">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" aria-hidden="true" data-id="element-45" />{' '}
                    El turno con el kinesiólogo que te dan para dentro de 3
                    semanas
                  </li>
                  <li className="flex items-start" role="listitem" data-id="element-46">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" aria-hidden="true" data-id="element-47" />{' '}
                    La sesión particular que cuesta $18.000 y te alivia 3 días
                  </li>
                  <li className="flex items-start" role="listitem" data-id="element-48">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" aria-hidden="true" data-id="element-49" />{' '}
                    La frustración de que a la semana siguiente vuelve
                    exactamente igual
                  </li>
                  <li className="flex items-start" role="listitem" data-id="element-50">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" aria-hidden="true" data-id="element-51" />{' '}
                    La duda de si con tu edad y tu trabajo esto ya no tiene
                    solución
                  </li>
                </ul>
              </FadeIn>

              <FadeIn delay={0.2} className="pt-8 text-center bg-navy-800/50 p-8 rounded-2xl border border-navy-700" data-id="element-52">
                <p className="text-2xl text-white font-serif mb-4" data-id="element-53">
                  ¿Cuánto te costó eso el año pasado?
                </p>
                <p className="text-gray-400 mb-6" data-id="element-54">
                  En plata. En tiempo. En energía. En calidad de vida.
                </p>
                <p className="text-xl text-gold-500 font-bold" data-id="element-55">
                  Si sos honesto... sabés que demasiado.
                </p>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* TRUTH SECTION */}
        <section aria-label="La verdad sobre el dolor" className="py-20 px-4 bg-navy-800 border-y border-gold-500/10" data-id="element-56">
          <div className="max-w-3xl mx-auto" data-id="element-57">
            <FadeIn data-id="element-58">
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-12 text-center" data-id="element-59">
                LA VERDAD QUE NADIE TE DICE
              </h2>
            </FadeIn>

            <div className="space-y-6 text-lg text-gray-300" data-id="element-60">
              <FadeIn data-id="element-61">
                <p data-id="element-62">El problema no es tu cuerpo.</p>
                <p className="text-xl text-white font-bold mt-2" data-id="element-63">
                  El problema es que tratás el síntoma y nunca la causa.
                </p>
              </FadeIn>

              <FadeIn data-id="element-64">
                <div className="my-10 text-center" data-id="element-65">
                  <h3 className="text-2xl md:text-3xl font-bold text-gold-400 font-serif leading-tight" data-id="element-66">
                    El 80% del dolor musculoesquelético crónico del argentino
                    adulto vienen de errores sistémicos y prevenibles.
                  </h3>
                </div>
              </FadeIn>

              <FadeIn data-id="element-67">
                <div className="bg-navy-900 p-8 rounded-2xl border-l-4 border-gold-500" data-id="element-68">
                  <p className="mb-4" data-id="element-69">No son errores del cuerpo.</p>
                  <p className="mb-4" data-id="element-70">
                    Son errores que ocurren porque no existe una estructura de
                    autocuidado progresiva y medible detrás de cada día.
                  </p>
                  <p className="text-white font-medium" data-id="element-71">
                    Tu cuerpo no te avisa que se está deteriorando. Te deja
                    funcionar. Y después llega el dolor.
                  </p>
                </div>
              </FadeIn>

              <FadeIn data-id="element-72">
                <div className="text-center space-y-4 text-xl mt-12" data-id="element-73">
                  <p className="text-gray-400" data-id="element-74">
                    La solución no es tomar más ibuprofeno. La solución no es ir
                    más seguido al kinesiólogo.
                  </p>
                  <p className="text-gold-500 font-bold text-2xl mt-4" data-id="element-75">
                    La solución es tener un sistema de autocuidado preventivo
                    que podés aplicar vos mismo.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* PRODUCT INTRO */}
        <section aria-label="Presentación del Método Kinesio-Ahorro" className="py-20 px-4 bg-navy-900 relative overflow-hidden" data-id="element-76">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[100px] pointer-events-none" aria-hidden="true" data-id="element-77"></div>

          <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-78">
            <FadeIn data-id="element-79">
              <div className="flex justify-center mb-6" data-id="element-80">
                <Activity className="w-16 h-16 text-gold-500" aria-hidden="true" data-id="element-81" />
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-white font-serif mb-4 drop-shadow-lg" data-id="element-82">
                MÉTODO KINESIO-AHORRO
              </h2>
              <h3 className="text-xl md:text-2xl text-gold-400 font-normal mb-2" data-id="element-83">
                El Sistema de los 6 Protocolos
              </h3>
              <p className="text-lg text-gray-400 italic mb-12" data-id="element-84">
                Pack 6 Tomos + 2 Bonos
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-85">
              <div className="max-w-2xl mx-auto text-center space-y-4 text-lg text-gray-300 bg-navy-800/50 p-8 rounded-2xl border border-gold-500/20" data-id="element-86">
                <p data-id="element-87">
                  No es un tutorial de YouTube que no explica nada. No es teoría
                  médica inaccesible. No es un método pensado para profesionales
                  de la salud.
                </p>
                <p className="text-xl text-white font-bold text-gold-500" data-id="element-88">
                  Es tu protocolo de autocuidado. Tu sistema. Tu independencia
                  del dolor.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* 6 TOMOS SECTION */}
        <section aria-label="Los 6 tomos del método" className="py-16 px-4 bg-navy-800" data-id="element-89">
          <div className="max-w-4xl mx-auto" data-id="element-90">
            <FadeIn data-id="element-91">
              <div className="text-center mb-12" data-id="element-92">
                <BookOpen className="w-10 h-10 text-gold-500 mx-auto mb-4" aria-hidden="true" data-id="element-93" />
                <h2 className="text-3xl font-bold text-white font-serif mb-4" data-id="element-94">
                  LOS 6 TOMOS
                </h2>
              </div>
            </FadeIn>

            <div className="space-y-6" data-id="element-95">
              {[{
              num: '1',
              emoji: '📘',
              title: 'SOS Cervical: Cuello y Zona Cervical',
              desc: 'Cómo identificar si tu dolor es muscular, postural o neural. Los 3 protocolos exactos para contractura aguda, cuello de texto y tensión por estrés. Paso a paso con diagramas para aplicarte vos solo en 15 minutos.'
            }, {
              num: '2',
              emoji: '📙',
              title: 'Blindaje Lumbar: Espalda Baja y Ciático',
              desc: 'La diferencia entre dolor lumbar, sacroilíaco y ciático. Cómo diagnosticarlo en 7 minutos sin ser médico. Los 4 protocolos completos incluyendo el plan anti-recaída lumbar para los que trabajan sentados o de pie todo el día.'
            }, {
              num: '3',
              emoji: '📗',
              title: 'Hombros y Manguito Rotador',
              desc: 'Por qué el dolor de hombro vuelve siempre y cómo cortar el ciclo. Los 4 protocolos completos: tendinitis, bursitis, manguito rotador y el protocolo específico para padelistas y tenistas. El sistema de tape que los deportistas de élite usan antes de cada partido.'
            }, {
              num: '4',
              emoji: '📕',
              title: 'Rodilla y Tobillo',
              desc: 'Los protocolos para condromalacia, esguince, inestabilidad y el sistema completo para runners. Cómo aplicar el tape antes de correr para que la rodilla aguante sin dolor. El protocolo de rehabilitación propioceptiva para el tobillo que cedió.'
            }, {
              num: '5',
              emoji: '📒',
              title: 'Manos, Muñecas y Codos',
              desc: 'El protocolo para túnel carpiano, epicondilitis lateral, epicondilitis medial y el sistema completo para trabajadores remotos. Por qué el mouse y el teclado te están destruyendo la muñeca y cómo prevenirlo en 15 minutos antes de arrancar la jornada.'
            }, {
              num: '6',
              emoji: '📓',
              title: 'Guía Maestra de Aplicación',
              desc: 'Todo lo que un kinesiólogo sabe y nunca te contó: los 7 tipos de corte, la física del tape, los 5 niveles de tensión, las técnicas avanzadas de kinesiología deportiva y el sistema mensual de mantenimiento para cero dolor crónico.'
            }].map((tomo, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-96">
                  <div className="bg-navy-900 border-l-4 border-gold-500 rounded-r-xl p-6 shadow-md" data-id="element-97">
                    <h3 className="text-xl font-bold text-white font-serif mb-2" data-id="element-98">
                      <span className="text-gold-500 mr-2" data-id="element-99">
                        {tomo.emoji} TOMO {tomo.num} —
                      </span>{' '}
                      {tomo.title}
                    </h3>
                    <p className="text-gray-300" data-id="element-100">{tomo.desc}</p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* 2 BONOS SECTION */}
        <section aria-label="Bonos incluidos" className="py-16 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-101">
          <div className="max-w-4xl mx-auto" data-id="element-102">
            <FadeIn data-id="element-103">
              <div className="text-center mb-12" data-id="element-104">
                <Gift className="w-10 h-10 text-gold-500 mx-auto mb-4" aria-hidden="true" data-id="element-105" />
                <h2 className="text-3xl font-bold text-white font-serif mb-4" data-id="element-106">
                  LOS 2 BONOS INCLUIDOS
                </h2>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-107">
              <FadeIn delay={0.1} className="h-full" data-id="element-108">
                <div className="bg-navy-800 border-t-4 border-green-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-109">
                  <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-110">
                    🟢 BONO #1
                    <br data-id="element-111" />
                    <span className="text-green-400" data-id="element-112">
                      Test Rápido "¿Qué Tengo?" — Diagnóstico Casero en 7
                      Minutos
                    </span>
                  </h3>
                  <p className="text-sm text-gray-300" data-id="element-113">
                    En menos de 7 minutos sabés exactamente qué tipo de dolor
                    tenés y qué protocolo aplicar de los 6 tomos. Sin adivinar.
                    Sin equivocarte. Sin aplicar el tape en la zona incorrecta.
                    Incluye el mapa completo de síntomas por zona, los tests de
                    autodiagnóstico para cada condición y el tracker de 7 días
                    para medir si el protocolo elegido está funcionando.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} className="h-full" data-id="element-114">
                <div className="bg-navy-800 border-t-4 border-blue-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-115">
                  <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-116">
                    🔵 BONO #2
                    <br data-id="element-117" />
                    <span className="text-blue-400" data-id="element-118">
                      Plan 14 Días "Cero Recaída" — Micro-Rutinas de 7 Minutos
                    </span>
                  </h3>
                  <p className="text-sm text-gray-300" data-id="element-119">
                    El dolor vuelve siempre porque la causa sigue ahí. Este plan
                    de 14 días ataca el ciclo completo con micro-rutinas de 7
                    minutos diarios (sin gimnasio, sin equipamiento) para que
                    los resultados del tape sean permanentes y no temporales.
                    Incluye el calendario imprimible de 14 días, el tracker de
                    sueño y estrés, el tracker de movimiento y pausas y el plan
                    de mantenimiento mensual.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section aria-label="Comparación antes y después" className="py-16 px-4 bg-navy-800" data-id="element-120">
          <div className="max-w-3xl mx-auto" data-id="element-121">
            <FadeIn data-id="element-122">
              <h2 className="text-3xl font-bold text-center text-white font-serif mb-10" data-id="element-123">
                LO QUE CAMBIA EN TU PRÓXIMA SEMANA
              </h2>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-124">
              <div className="rounded-2xl border border-gold-500/30 shadow-xl overflow-hidden" data-id="element-125">
                <table className="w-full text-left border-collapse" role="table" data-id="element-126">
                  <caption className="sr-only" data-id="element-127">
                    Comparación: sin el método vs con Kinesio-Ahorro
                  </caption>
                  <thead data-id="element-128">
                    <tr className="bg-navy-900 border-b border-gold-500/30" data-id="element-129">
                      <th scope="col" className="p-3 md:p-5 text-red-400 font-bold text-center w-1/2 text-xs md:text-base" data-id="element-130">
                        ❌ Sin el Método
                      </th>
                      <th scope="col" className="p-3 md:p-5 text-green-400 font-bold text-center w-1/2 border-l border-navy-700 bg-gold-500/5 text-xs md:text-base" data-id="element-131">
                        ✅ Con Kinesio-Ahorro
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-700 bg-navy-800 text-xs md:text-base" data-id="element-132">
                    {[{
                    bad: 'Te levantás esperando que el dolor se vaya solo',
                    good: 'Te levantás con un protocolo claro para los días malos'
                  }, {
                    bad: 'Esperás 3 semanas el turno del kinesiólogo',
                    good: 'Aplicás el protocolo en 15 minutos en tu casa'
                  }, {
                    bad: 'Gastás $18.000 por sesión que te alivia 3 días',
                    good: 'Invertís una vez y tenés el sistema para siempre'
                  }, {
                    bad: 'El dolor vuelve siempre igual o peor',
                    good: 'El Plan 14 Días corta el ciclo de recaídas'
                  }, {
                    bad: 'No sabés si lo que tenés es muscular o nervioso',
                    good: 'El test de diagnóstico te lo dice en 7 minutos'
                  }, {
                    bad: 'Dependés del sistema de salud para funcionar',
                    good: 'Tenés independencia real para manejar tu dolor'
                  }, {
                    bad: 'Tomás ibuprofeno como primera solución',
                    good: 'Tratás la causa, no el síntoma'
                  }].map((row, idx) => <tr key={idx} data-id="element-133">
                        <td className="p-3 md:p-4 text-gray-400 text-center" data-id="element-134">
                          {row.bad}
                        </td>
                        <td className="p-3 md:p-4 text-gold-300 font-medium border-l border-navy-700 text-center bg-gold-500/5" data-id="element-135">
                          {row.good}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section aria-label="Testimonios de usuarios" className="py-20 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-136">
          <div className="max-w-4xl mx-auto" data-id="element-137">
            <FadeIn data-id="element-138">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-16" data-id="element-139">
                LO QUE DICEN LOS QUE YA LO APLICARON
              </h2>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-140">
              {[{
              stars: 5,
              title: '"Me levanté sin dolor por primera vez en 3 años"',
              quote: 'Tenía dolor de cuello todas las mañanas. Era lo primero que sentía al despertar. Tres días después de aplicar el protocolo del Tomo 1 me desperté sin dolor. Pensé que era casualidad. Llevo 6 semanas y sigue igual.',
              name: 'Patricia M.',
              role: 'Trabajadora administrativa',
              location: 'Buenos Aires, CABA',
              img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
            }, {
              stars: 5,
              title: '"Me ahorré 5 sesiones en el primer mes"',
              quote: 'Calculé lo que gasté en kinesiología el año pasado. Fueron 14 sesiones a $16.000 cada una. Con el Método Kinesio-Ahorro no fui ni una vez en el primer mes. El protocolo lumbar me dio lo mismo que me daban en el consultorio.',
              name: 'Gustavo R.',
              role: 'Contador',
              location: 'Córdoba Capital',
              img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
            }, {
              stars: 5,
              title: '"El diagnóstico del Bono 1 me cambió todo"',
              quote: 'Siempre pensé que tenía ciático. Me trataba para ciático. El test del Bono 1 me mostró que era sacroilíaco. Cambié el protocolo y en 4 días mejoró lo que en meses de tratamiento incorrecto no había mejorado.',
              name: 'Verónica L.',
              role: 'Docente',
              location: 'Rosario, Santa Fe',
              img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
            }, {
              stars: 5,
              title: '"Mi marido dejó de tomar ibuprofeno todos los días"',
              quote: 'Mi marido tomaba ibuprofeno como caramelos. Lumbar crónico de 8 años. Empezamos el Plan 14 Días del Bono 2 y en la segunda semana dejó de tomarlo. No lo puedo creer todavía.',
              name: 'Claudia B.',
              role: 'Ama de casa',
              location: 'Mendoza',
              img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
            }, {
              stars: 5,
              title: '"El protocolo de hombro me salvó el pádel"',
              quote: 'Tenía dolor de hombro que me impedía terminar los partidos. El Tomo 3, protocolo del padelista, fue exactamente lo que necesitaba. Ahora me lo aplico antes de cada partido y terminé el último torneo sin dolor.',
              name: 'Sebastián T.',
              role: 'Padelista amateur',
              location: 'Tucumán',
              img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
            }].map((t, idx) => <FadeIn key={idx} delay={idx * 0.08} data-id="element-141">
                  <article className="bg-navy-800 p-6 rounded-2xl border border-navy-700 h-full flex flex-col" data-id="element-142">
                    <div className="flex text-gold-500 mb-3" aria-label={`Calificación: ${t.stars} de 5 estrellas`} role="img" data-id="element-143">
                      {[...Array(5)].map((_, i) => <svg key={i} className={`w-4 h-4 ${i < t.stars ? 'fill-gold-500 text-gold-500' : 'fill-navy-600 text-navy-600'}`} viewBox="0 0 20 20" aria-hidden="true" data-id="element-144">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-145" />
                        </svg>)}
                    </div>
                    <h4 className="text-white font-bold mb-3 text-sm" data-id="element-146">
                      {t.title}
                    </h4>
                    <p className="text-gray-400 text-sm italic leading-relaxed flex-1" data-id="element-147">
                      "{t.quote}"
                    </p>
                    <div className="mt-5 pt-4 border-t border-navy-700 flex items-center gap-3" data-id="element-148">
                      <img src={t.img} alt={`Foto de ${t.name}`} className="w-10 h-10 rounded-full object-cover border-2 border-navy-600" width={40} height={40} loading="lazy" decoding="async" data-id="element-149" />
                      <div data-id="element-150">
                        <p className="text-white font-bold text-sm" data-id="element-151">{t.name}</p>
                        <p className="text-gray-500 text-xs" data-id="element-152">
                          {t.role} · {t.location}
                        </p>
                      </div>
                    </div>
                  </article>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* UPSELL INTRO */}
        <section aria-label="Complemento Premium" className="py-20 px-4 bg-navy-900 border-t border-gold-500/20 relative overflow-hidden" data-id="element-153">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[120px] pointer-events-none" aria-hidden="true" data-id="element-154"></div>
          <div className="max-w-3xl mx-auto relative z-10" data-id="element-155">
            <FadeIn data-id="element-156">
              <div className="text-center mb-12" data-id="element-157">
                <span className="text-4xl mb-4 block" aria-hidden="true" data-id="element-158">
                  ⚡
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-3" data-id="element-159">
                  UN MOMENTO — ANTES DE CONFIRMAR TU COMPRA
                </h2>
                <p className="text-xl text-gold-400 italic" data-id="element-160">
                  Agregá esto a tu pack y completá el sistema en un solo paso
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-161">
              <div className="text-center mb-12" data-id="element-162">
                <p className="text-lg text-gray-300 mb-2" data-id="element-163">
                  El Método de los 6 Tomos te da los protocolos para manejar el
                  dolor.
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white font-serif" data-id="element-164">
                  Pero ¿quién te dice exactamente qué cinta comprar, dónde
                  conseguirla más barata y cómo hacer que dure 7 días en lugar
                  de 2?
                </h3>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-165">
              <div className="space-y-5 text-lg text-gray-300 mb-12" data-id="element-166">
                <p data-id="element-167">Aprendiste a aplicar el tape.</p>
                <p data-id="element-168">Elegiste el protocolo correcto.</p>
                <p className="text-white font-medium" data-id="element-169">
                  Y sin embargo... la cinta se despega a las 12 horas. O
                  compraste una marca que no pega bien. O gastaste el doble de
                  lo que debías porque no sabías dónde comprar.
                </p>
                <p className="mt-6" data-id="element-170">¿Por qué?</p>
                <p data-id="element-171">
                  Porque{' '}
                  <strong className="text-white" data-id="element-172">dominar los protocolos</strong>{' '}
                  y{' '}
                  <strong className="text-white" data-id="element-173">
                    dominar la implementación práctica
                  </strong>{' '}
                  son dos cosas distintas.
                </p>
                <p data-id="element-174">Y la mayoría de los usuarios solo tiene acceso a una.</p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-175">
              <div className="text-center mb-10" data-id="element-176">
                <p className="text-gold-500 font-bold tracking-widest uppercase text-sm mb-4" data-id="element-177">
                  🏆 COMPLETÁ TU PACK CON:
                </p>
                <h2 className="text-5xl md:text-6xl font-bold text-white font-serif mb-3" data-id="element-178">
                  KINESIO-PREMIUM
                </h2>
                <h3 className="text-xl md:text-2xl text-gold-400 mb-1" data-id="element-179">
                  Kit de Implementación y Ahorro
                </h3>
                <p className="text-gray-400 italic" data-id="element-180">
                  Para el Usuario que Quiere Resultados Desde el Primer Día
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-181">
              <div className="bg-navy-800/60 border border-navy-600 rounded-2xl p-8 space-y-5" data-id="element-182">
                {[{
                title: '🗂️ Las 20 Fichas XL de Auto-Aplicación',
                desc: 'Una ficha por condición. Diseñadas para ver en el celular o imprimir. Con el sistema de colores de tensión, las flechas de dirección para el espejo y el checklist pre-aplicación. En 30 segundos sabés exactamente qué hacer sin abrir ningún tomo.'
              }, {
                title: '💰 Guía Maestra de Insumos',
                desc: 'Ranking honesto de marcas disponibles en farmacias, ortopedias y Mercado Libre. Las que pegan de verdad y las que se caen en 6 horas. Dónde comprar hasta un 50% más barato que en Farmacity. La calculadora de cortes para que un rollo de 5 metros te rinda el doble.'
              }, {
                title: '📐 Calculadora de Cortes',
                desc: 'Cuántos centímetros exactos necesitás para cada protocolo. Cuántos protocolos rinde un rollo completo. Cómo no desperdiciar ni un centímetro de cinta.'
              }].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-183">
                    <Check className="w-6 h-6 text-green-500 shrink-0 mt-0.5" aria-hidden="true" data-id="element-184" />
                    <p className="text-gray-200" data-id="element-185">
                      <strong className="text-white" data-id="element-186">{item.title}</strong> —{' '}
                      {item.desc}
                    </p>
                  </div>)}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* OFFER CARDS */}
        <section aria-label="Elegí tu opción de compra" className="py-20 px-4 bg-navy-900 relative" data-id="element-187">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold-900/10 via-navy-900 to-navy-900 -z-10" aria-hidden="true" data-id="element-188"></div>
          <div className="max-w-4xl mx-auto" data-id="element-189">
            <FadeIn data-id="element-190">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-4" data-id="element-191">
                ELEGÍ TU OPCIÓN
              </h2>
              <p className="text-center text-gray-400 mb-12" data-id="element-192">
                Seleccioná el pack que mejor se adapte a tu objetivo.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-193">
              <div className="max-w-md mx-auto bg-[#111827] p-4 md:p-6 rounded-[2rem] border border-navy-600 shadow-2xl" data-id="element-194">
                {/* Offer radio group */}
                <div role="radiogroup" aria-label="Opciones de compra" data-id="element-195">
                  {/* Pro Card (First) */}
                  <div role="radio" aria-checked={selectedOffer === 'pro'} tabIndex={selectedOffer === 'pro' ? 0 : -1} onClick={() => setSelectedOffer('pro')} onKeyDown={handleOfferKeyDown('pro')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-4 flex justify-between items-start ${selectedOffer === 'pro' ? 'border-green-500 bg-navy-800/80 shadow-[0_0_15px_rgba(34,197,94,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-196">
                    <div className="flex items-start gap-4" data-id="element-197">
                      <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'pro' ? 'border-green-500' : 'border-gray-500'}`} aria-hidden="true" data-id="element-198">
                        {selectedOffer === 'pro' && <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-199" />}
                      </div>
                      <div data-id="element-200">
                        <div className="flex items-center gap-2 mb-2" data-id="element-201">
                          <Trophy className="w-5 h-5 text-gold-500 fill-gold-500" aria-hidden="true" data-id="element-202" />
                          <span className="font-bold text-white text-lg tracking-wide" data-id="element-203">
                            KINESIO PREMIUM
                          </span>
                        </div>
                        <div className="flex flex-col gap-2 mb-3 items-start" data-id="element-204">
                          <span className="bg-green-600 text-white text-[10px] sm:text-xs font-bold px-2 py-1 rounded flex items-center gap-1 uppercase tracking-wider" data-id="element-205">
                            <Star className="w-3 h-3 fill-white" aria-hidden="true" data-id="element-206" />{' '}
                            MÁS ELEGIDO
                          </span>
                          <span className="border border-green-500/50 text-green-400 text-[10px] sm:text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider" data-id="element-207">
                            AHORRÁS $10.000
                          </span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1.5" data-id="element-208">
                          <li data-id="element-209">6 tomos + 2 bonos</li>
                          <li className="text-white font-medium" data-id="element-210">
                            + Kit Kinesio-Premium
                          </li>
                          <li className="text-gray-500 text-xs pl-2" data-id="element-211">
                            - 20 Fichas XL de Auto-Aplicación
                          </li>
                          <li className="text-gray-500 text-xs pl-2" data-id="element-212">
                            - Guía de Insumos Argentina
                          </li>
                          <li className="text-gray-500 text-xs pl-2" data-id="element-213">
                            - Calculadora de Cortes
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-214">
                      <p className="text-gray-500 line-through text-sm font-medium" data-id="element-215">
                        $39.990
                      </p>
                      <p className="text-3xl font-bold text-green-500" data-id="element-216">
                        $29.990
                      </p>
                    </div>
                  </div>

                  {/* Base Card (Second) */}
                  <div role="radio" aria-checked={selectedOffer === 'base'} tabIndex={selectedOffer === 'base' ? 0 : -1} onClick={() => setSelectedOffer('base')} onKeyDown={handleOfferKeyDown('base')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-6 flex justify-between items-center ${selectedOffer === 'base' ? 'border-green-500 bg-navy-800/80 shadow-[0_0_15px_rgba(34,197,94,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-217">
                    <div className="flex items-start gap-4" data-id="element-218">
                      <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'base' ? 'border-green-500' : 'border-gray-500'}`} aria-hidden="true" data-id="element-219">
                        {selectedOffer === 'base' && <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-220" />}
                      </div>
                      <div data-id="element-221">
                        <div className="flex items-center gap-2 mb-1" data-id="element-222">
                          <Heart className="w-5 h-5 text-green-500 fill-green-500" aria-hidden="true" data-id="element-223" />
                          <span className="font-bold text-white text-lg tracking-wide" data-id="element-224">
                            PACK BASE
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm" data-id="element-225">
                          6 tomos + 2 bonos
                        </p>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-226">
                      <p className="text-gray-500 line-through text-sm font-medium" data-id="element-227">
                        $36.500
                      </p>
                      <p className="text-2xl font-bold text-white" data-id="element-228">$19.990</p>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <a href={selectedOffer === 'base' ? 'https://fegion.shop/cart/46204280733835:1' : 'https://fegion.shop/cart/46204306718859:1'} target="_blank" rel="noopener noreferrer" className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-lg py-4 px-6 rounded-xl shadow-[0_0_20px_rgba(46,125,50,0.4)] transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 uppercase tracking-wide" aria-label={`Comprar ${selectedOffer === 'base' ? 'Pack Base por $19.990' : 'Combo Premium por $29.990'}`} data-id="element-229">
                  <Heart className="w-5 h-5 fill-white" aria-hidden="true" data-id="element-230" />
                  EMPEZAR HOY — $
                  {selectedOffer === 'base' ? '19.990' : '29.990'}
                </a>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* PACK COMPARISON TABLE */}
        <section aria-label="Comparación de packs" className="py-16 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-231">
          <div className="max-w-3xl mx-auto" data-id="element-232">
            <FadeIn data-id="element-233">
              <div className="rounded-2xl border border-gold-500/30 shadow-xl overflow-hidden" data-id="element-234">
                <table className="w-full text-left border-collapse" role="table" data-id="element-235">
                  <caption className="sr-only" data-id="element-236">
                    Comparación de contenido entre Pack Base y Combo Premium
                  </caption>
                  <thead data-id="element-237">
                    <tr className="bg-navy-900 border-b border-gold-500/30" data-id="element-238">
                      <th scope="col" className="p-2 md:p-5 text-gray-400 font-medium text-xs md:text-base w-[40%]" data-id="element-239">
                        <span className="sr-only" data-id="element-240">Característica</span>
                      </th>
                      <th scope="col" className="p-2 md:p-5 text-center border-l border-navy-700 text-xs md:text-base w-[30%]" data-id="element-241">
                        <span className="text-white font-bold" data-id="element-242">
                          <span className="hidden md:inline" aria-hidden="true" data-id="element-243">
                            📦{' '}
                          </span>
                          PACK BASE
                        </span>
                      </th>
                      <th scope="col" className="p-2 md:p-5 text-center border-l border-navy-700 bg-gold-500/5 text-xs md:text-base w-[30%]" data-id="element-244">
                        <span className="text-gold-400 font-bold" data-id="element-245">
                          <span className="hidden md:inline" aria-hidden="true" data-id="element-246">
                            🏆{' '}
                          </span>
                          COMBO PREMIUM
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-700 bg-navy-800 text-xs md:text-base" data-id="element-247">
                    {[{
                    label: '6 Tomos Método Kinesio-Ahorro',
                    base: true,
                    pro: true
                  }, {
                    label: 'Bono #1: Test Diagnóstico',
                    base: true,
                    pro: true
                  }, {
                    label: 'Bono #2: Plan 14 Días',
                    base: true,
                    pro: true
                  }, {
                    label: '20 Fichas XL de Auto-Aplicación',
                    base: false,
                    pro: true
                  }, {
                    label: 'Guía de Insumos Argentina',
                    base: false,
                    pro: true
                  }, {
                    label: 'Calculadora de Cortes',
                    base: false,
                    pro: true
                  }, {
                    label: 'Ranking de Marcas Locales',
                    base: false,
                    pro: true
                  }, {
                    label: 'Guía de Distribuidores Baratos',
                    base: false,
                    pro: true
                  }].map((row, idx) => <tr key={idx} data-id="element-248">
                        <td className="p-2 md:p-4 text-gray-300 font-medium" data-id="element-249">
                          {row.label}
                        </td>
                        <td className="p-2 md:p-4 text-center border-l border-navy-700" data-id="element-250">
                          {row.base ? <span className="text-green-500" aria-label="Incluido" data-id="element-251">
                              ✅
                            </span> : <span className="text-red-500" aria-label="No incluido" data-id="element-252">
                              ❌
                            </span>}
                        </td>
                        <td className="p-2 md:p-4 text-center border-l border-navy-700 bg-gold-500/5" data-id="element-253">
                          {row.pro ? <span className="text-green-500" aria-label="Incluido" data-id="element-254">
                              ✅
                            </span> : <span className="text-red-500" aria-label="No incluido" data-id="element-255">
                              ❌
                            </span>}
                        </td>
                      </tr>)}
                    <tr className="bg-navy-900/50 border-t-2 border-gold-500/30" data-id="element-256">
                      <td className="p-2 md:p-4 text-white font-bold text-xs md:text-base" data-id="element-257">
                        PRECIO
                      </td>
                      <td className="p-2 md:p-4 text-center border-l border-navy-700 text-white font-bold text-sm md:text-lg" data-id="element-258">
                        $19.990
                      </td>
                      <td className="p-2 md:p-4 text-center border-l border-navy-700 bg-gold-500/5 text-gold-400 font-bold text-sm md:text-lg" data-id="element-259">
                        $29.990
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </FadeIn>

            {/* Savings callout */}
            <FadeIn delay={0.2} data-id="element-260">
              <div className="mt-10 text-center bg-navy-900/50 p-6 rounded-2xl border border-gold-500/20" data-id="element-261">
                <p className="text-gold-400 font-bold text-xl mb-2" data-id="element-262">
                  💡 Solo $10.000 ARS más.
                </p>
                <p className="text-gray-300" data-id="element-263">
                  El Kit Kinesio-Premium por separado vale $19.990 ARS.
                </p>
                <p className="text-gold-400 italic text-sm mt-1" data-id="element-264">
                  Agregándolo ahora lo llevás a la mitad del precio.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* DUAL CTA SECTION */}
        <section aria-label="Opciones de compra final" className="py-20 px-4 bg-navy-900 relative" data-id="element-265">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-green-900/10 via-navy-900 to-navy-900 -z-10" aria-hidden="true" data-id="element-266"></div>
          <div className="max-w-2xl mx-auto text-center" data-id="element-267">
            <FadeIn data-id="element-268">
              <p className="text-red-500 font-bold tracking-widest uppercase text-sm mb-8" data-id="element-269">
                🔴 ELEGÍ TU OPCIÓN:
              </p>

              {/* Option A — Combo Pro */}
              <div className="bg-navy-800 border-2 border-green-500 rounded-2xl p-8 md:p-10 mb-6 shadow-[0_0_30px_rgba(34,197,94,0.1)]" data-id="element-270">
                <p className="text-green-400 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-271">
                  OPCIÓN A — COMBO COMPLETO RECOMENDADO
                </p>
                <h3 className="text-2xl font-bold text-white font-serif mb-4" data-id="element-272">
                  MÉTODO KINESIO-AHORRO + Kit Kinesio-Premium
                </h3>
                <div className="flex items-center justify-center gap-3 mb-6" data-id="element-273">
                  <span className="text-gray-500 line-through text-xl" data-id="element-274">
                    $39.990
                  </span>
                  <ArrowRight className="w-5 h-5 text-gold-500" aria-hidden="true" data-id="element-275" />
                  <span className="text-3xl font-black text-white font-serif" data-id="element-276">
                    $29.990{' '}
                    <span className="text-base text-gray-400 font-sans font-normal" data-id="element-277">
                      ARS
                    </span>
                  </span>
                </div>
                <a href="https://fegion.shop/cart/46204306718859:1" target="_blank" rel="noopener noreferrer" aria-label="Comprar Combo Completo Premium por $29.990 ARS" className="w-full block bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-lg py-5 px-8 rounded-xl shadow-[0_0_25px_rgba(46,125,50,0.4)] transition-all transform hover:scale-[1.02] uppercase tracking-wide mb-4 text-center" data-id="element-278">
                  → SÍ, QUIERO EL COMBO COMPLETO ←
                </a>
                <p className="text-gray-400 text-sm" data-id="element-279">
                  ✅ Todo incluido · ✅ Acceso inmediato · ✅ Garantía 7 días
                </p>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 my-8" data-id="element-280">
                <div className="flex-1 h-px bg-navy-700" data-id="element-281"></div>
                <span className="text-gray-500 font-medium" data-id="element-282">— o —</span>
                <div className="flex-1 h-px bg-navy-700" data-id="element-283"></div>
              </div>

              {/* Option B — Pack Base */}
              <div className="mb-8" data-id="element-284">
                <p className="text-gray-400 uppercase tracking-wider text-sm mb-2" data-id="element-285">
                  OPCIÓN B — Solo el Pack Base
                </p>
                <p className="text-xl text-white font-bold mb-4" data-id="element-286">$19.990 ARS</p>
                <a href="https://fegion.shop/cart/46204280733835:1" target="_blank" rel="noopener noreferrer" aria-label="Comprar solo el Pack Base por $19.990 ARS" className="w-full block bg-[#D4874D] hover:bg-[#C07843] text-white font-bold text-base py-4 px-8 rounded-xl shadow-[0_0_15px_rgba(212,135,77,0.25)] transition-all transform hover:scale-[1.02] mb-2 text-center" data-id="element-287">
                  No gracias, prefiero solo el Pack Base del Método
                  Kinesio-Ahorro
                </a>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* FINAL PERSUASION SECTION */}
        <section aria-label="Decisión final" className="py-20 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-288">
          <div className="max-w-3xl mx-auto text-center" data-id="element-289">
            <FadeIn data-id="element-290">
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-8" data-id="element-291">
                UNA ÚLTIMA COSA ANTES DE DECIDIR
              </h2>
              <div className="space-y-6 text-lg text-gray-300 mb-12 text-left bg-navy-900 p-8 rounded-2xl border border-navy-700" data-id="element-292">
                <p data-id="element-293">Pensá en el dolor de mañana a la mañana.</p>
                <p className="text-xl text-white font-bold" data-id="element-294">¿Va a estar ahí?</p>
                <p data-id="element-295">
                  Si la respuesta es probablemente sí... tenés dos opciones.
                </p>
                <div className="pl-4 border-l-2 border-red-500/50 space-y-2 mt-6" data-id="element-296">
                  <p data-id="element-297">
                    <strong className="text-red-400" data-id="element-298">Opción 1:</strong> Seguir
                    haciendo lo que hacías. El turno en 3 semanas. El ibuprofeno
                    de siempre. La sesión de $18.000 que dura 3 días. Y el ciclo
                    que empieza de nuevo.
                  </p>
                </div>
                <div className="pl-4 border-l-2 border-green-500/50 space-y-2 mt-6" data-id="element-299">
                  <p data-id="element-300">
                    <strong className="text-green-400" data-id="element-301">Opción 2:</strong> Tener
                    el sistema completo. El protocolo exacto para tu zona. El
                    diagnóstico en 7 minutos. El plan de 14 días para que no
                    vuelva. Y si elegís el Combo Premium, las fichas XL para
                    aplicarlo en 30 segundos sin pensar.
                  </p>
                </div>
                <p className="mt-8" data-id="element-302">
                  Una sola sesión de kinesiología privada cuesta más que el
                  Combo Completo. Y esa sesión te alivia 3 días.
                </p>
                <p className="text-gold-400 font-medium" data-id="element-303">
                  El Método Kinesio-Ahorro te acompaña para siempre.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-304">
              <h3 className="text-2xl md:text-3xl font-bold text-white font-serif mb-8" data-id="element-305">
                ¿Cuántas veces más vas a esperar que el dolor se vaya solo?
              </h3>

              <div className="max-w-xl mx-auto space-y-4" data-id="element-306">
                <a href="https://fegion.shop/cart/46204306718859:1" target="_blank" rel="noopener noreferrer" aria-label="Comprar Combo Completo Premium por $29.990 ARS" className="w-full block bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-lg py-5 px-8 rounded-xl shadow-[0_0_25px_rgba(46,125,50,0.4)] transition-all transform hover:scale-[1.02] uppercase tracking-wide text-center" data-id="element-307">
                  → SÍ, QUIERO EL COMBO COMPLETO — $29.990 ARS ←
                </a>
                <a href="https://fegion.shop/cart/46204280733835:1" target="_blank" rel="noopener noreferrer" aria-label="Comprar solo el Pack Base por $19.990 ARS" className="block text-gray-400 hover:text-white text-sm transition-colors text-center underline" data-id="element-308">
                  No gracias, prefiero solo el Pack Base — $19.990 ARS
                </a>
                <p className="text-xs text-gray-500 mt-6" data-id="element-309">
                  🔒 Pago único · Acceso inmediato · 100% Digital · Garantía 7
                  días
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* GUARANTEE */}
        <section aria-label="Garantía de devolución" className="py-16 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-310">
          <div className="max-w-2xl mx-auto" data-id="element-311">
            <FadeIn data-id="element-312">
              <div className="bg-navy-800 border border-gold-500/30 rounded-2xl p-8 text-center shadow-lg" data-id="element-313">
                <Lock className="w-12 h-12 text-gold-500 mx-auto mb-4" aria-hidden="true" data-id="element-314" />
                <h2 className="text-2xl font-bold text-white font-serif mb-4" data-id="element-315">
                  🔒 GARANTÍA EN AMBAS OPCIONES
                </h2>
                <p className="text-gray-300 text-lg mb-4" data-id="element-316">
                  7 días de garantía total. Devolución del 100% sin preguntas.
                </p>
                <p className="text-gray-400 text-sm mb-6" data-id="element-317">
                  Sea cual sea la opción que elijas — estás completamente
                  protegido. Si por cualquier razón sentís que no es lo que
                  prometemos, escribinos dentro de los 7 días posteriores a la
                  compra y te devolvemos el dinero completo. Sin formularios.
                  Sin preguntas. Sin demoras.
                </p>
                <p className="text-gold-400 font-medium" data-id="element-318">
                  El riesgo es completamente nuestro. La mejora es completamente
                  tuya.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="py-10 px-4 bg-navy-900 border-t border-navy-700 text-center" data-id="element-319">
        <Activity className="w-6 h-6 text-gold-500/50 mx-auto mb-3" aria-hidden="true" data-id="element-320" />
        <p className="text-gold-500/70 font-serif italic text-sm mb-2" data-id="element-321">
          by MÉTODO KINESIO-AHORRO 🇦🇷
        </p>
        <p className="text-gray-500 text-xs max-w-md mx-auto" data-id="element-322">
          Para el argentino adulto que no puede permitirse depender del sistema
          de salud para funcionar cada día.
        </p>
      </footer>
    </div>;
}